import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendBulkEmail, getAllEmailAddresses } from '../../services/EmailService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getAllEmailTemplates } from '../../services/TemplatesService';
import { useHtmlEditor } from '../../hooks/useHtmlEditor';
import { QUILL_MODULES, QUILL_FORMATS } from '../../constants/quillConfig';
import { initializeQuillPolyfill } from '../../utils/quillPolyfill';

// Initialize Quill polyfill
initializeQuillPolyfill();

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function NewEmail() {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [status, setStatus] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const navigate = useNavigate();
  const {
    editorMode,
    richTextContent,
    htmlContent,
    setRichTextContent,
    setHtmlContent,
    formatHtml,
    handleModeSwitch,
    getActiveContent,
    initializeContent,
    resetContent,
  } = useHtmlEditor();

  // Email search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const searchRef = useRef(null);

  // Click outside handler to close search results
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        const data = await getAllEmailTemplates();
        setTemplates(data);
      } catch (err) {
        toast.error('Failed to load templates');
      }
    }
    fetchTemplates();
  }, []);

  // Search for email addresses
  useEffect(() => {
    async function searchEmailAddresses() {
      if (debouncedSearchTerm.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const data = await getAllEmailAddresses({
          pageNumber: 1,
          pageSize: 10,
          searchTerm: debouncedSearchTerm,
          sortBy: 'address',
          sortDescending: false
        });
        setSearchResults(data.items || []);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Failed to search email addresses:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }

    searchEmailAddresses();
  }, [debouncedSearchTerm]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    const template = templates.find(t => String(t.id) === String(templateId));
    if (template) {
      setSubject(template.subject || '');
      initializeContent(template.body || '');
    } else {
      setSubject('');
      resetContent();
    }
  };

  const handleEmailSelect = (emailAddress) => {
    const currentEmails = to.split(',').map(email => email.trim()).filter(Boolean);
    if (!currentEmails.includes(emailAddress)) {
      const newEmailList = [...currentEmails, emailAddress];
      setTo(newEmailList.join(', '));
    }
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim().length >= 2) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');
    try {
      const emailList = to.split(',').map(email => email.trim()).filter(Boolean);
      const body = getActiveContent();
      await sendBulkEmail({ to: emailList, subject, body, EmailTemplateId: selectedTemplateId ? Number(selectedTemplateId) : undefined });
      setStatus('Email sent successfully!');
      setTo('');
      setSubject('');
      resetContent();
      toast.success('Email sent successfully!', { position: 'top-right', autoClose: 2500 });
      navigate('/email-messages');
    } catch (error) {
      setStatus('Failed to send email.');
      toast.error('Failed to send email.', { position: 'top-right', autoClose: 2500 });
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 900 }}>
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Send New Email</h4>
      </div>
      <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm border">
        <div className="mb-3">
          <label className="form-label fw-semibold">Select a Template</label>
          <select className="form-select" value={selectedTemplateId} onChange={handleTemplateChange}>
            <option value="">-- No Template --</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.templateName}</option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label htmlFor="emailTo" className="form-label fw-semibold">Recipient Email *</label>
          <div className="position-relative">
            <textarea
              className="form-control"
              id="emailTo"
              rows="3"
              placeholder="Enter email addresses separated by commas (e.g., user1@example.com, user2@example.com)"
              value={to}
              onChange={e => setTo(e.target.value)}
              required
            ></textarea>
            <div className="mt-2" ref={searchRef}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search for existing email addresses..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchTerm.trim().length >= 2) {
                      setShowSearchResults(true);
                    }
                  }}
                />
                {isSearching && (
                  <span className="input-group-text">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </span>
                )}
                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setShowSearchResults(false);
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
              {showSearchResults && searchResults.length > 0 && (
                <div className="position-absolute w-100 mt-1" style={{ zIndex: 1000 }}>
                  <div className="card border shadow-sm">
                    <div className="card-header py-2">
                      <small className="text-muted">Select an email address to add:</small>
                    </div>
                    <div className="card-body p-0">
                      <div className="list-group list-group-flush">
                        {searchResults.map((email) => (
                          <button
                            key={email.address}
                            type="button"
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            onClick={() => handleEmailSelect(email.address)}
                          >
                            <div>
                              <strong>{email.address}</strong>
                              {email.fullName && (
                                <div className="text-muted small">{email.fullName}</div>
                              )}
                            </div>
                            <span className={`badge bg-${email.isBlocked ? 'danger' : 'success'}`}>
                              {email.isBlocked ? 'Blocked' : 'Active'}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {showSearchResults && searchResults.length === 0 && debouncedSearchTerm.trim().length >= 2 && !isSearching && (
                <div className="position-absolute w-100 mt-1" style={{ zIndex: 1000 }}>
                  <div className="card border shadow-sm">
                    <div className="card-body p-3 text-center text-muted">
                      No email addresses found matching "{debouncedSearchTerm}"
                    </div>
                  </div>
                </div>
              )}
            </div>
            <small className="text-muted">Enter email addresses separated by commas. Multiple recipients are supported. Use the search above to find existing email addresses.</small>
          </div>
        </div>
        <div className="mb-3">
          <label htmlFor="emailSubject" className="form-label fw-semibold">Subject *</label>
          <input
            type="text"
            className="form-control"
            id="emailSubject"
            placeholder="Enter email subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-semibold mb-0">Email Body *</label>
            <div className="btn-group btn-group-sm" role="group">
              <input
                type="radio"
                className="btn-check"
                name="emailEditorMode"
                id="richEmailEditor"
                checked={editorMode === 'rich'}
                onChange={() => handleModeSwitch('rich')}
              />
              <label className="btn btn-outline-primary" htmlFor="richEmailEditor">Rich Text</label>

              <input
                type="radio"
                className="btn-check"
                name="emailEditorMode"
                id="htmlEmailEditor"
                checked={editorMode === 'html'}
                onChange={() => handleModeSwitch('html')}
              />
              <label className="btn btn-outline-primary" htmlFor="htmlEmailEditor">HTML</label>
            </div>
          </div>

          {editorMode === 'rich' ? (
            <ReactQuill
              value={richTextContent}
              onChange={setRichTextContent}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Enter your email message here..."
              theme="snow"
            />
          ) : (
            <textarea
              className="form-control"
              value={htmlContent}
              onChange={(e) => setHtmlContent(formatHtml(e.target.value))}
              rows={18}
              placeholder={'Enter your HTML email template here...'}
              style={{
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '13px',
                lineHeight: '1.4',
                tabSize: 2
              }}
              required
            />
          )}
        </div>
        {status && <div className="mb-4"><small className="text-secondary">{status}</small></div>}
      </form>
      
      {/* Move buttons outside the form for better accessibility */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button 
          type="button" 
          className="btn btn-outline-secondary px-4" 
          onClick={() => navigate(-1)}
          style={{ minWidth: '100px' }}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn btn-primary px-4"
          onClick={handleSubmit}
          style={{ minWidth: '100px' }}
        >
          Send Email
        </button>
      </div>
    </div>
  );
}

export default NewEmail; 